import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { RouteDialogContext } from "arena/components/RouteDialog";
import ProxyPageOverrideContext from "arena/components/ProxyPage/ProxyPageOverrideContext";
import Channel from "arena/components/Channel";
import Video from "arena/components/Video";
import Stream from "arena/components/Stream";
import Product from "arena/components/Product";
import Post from "arena/components/Post";
import DigitalAsset from "arena/components/DigitalAsset";
import { useArena } from "core/components/ArenaProvider";

const CHANNEL = "CHANNEL";
const VIDEO = "VIDEO";
const STREAM = "STREAM";

function ProxyPageContent({ arenaProxyPage }) {
  const { content, product, post, digitalAsset, tag } = arenaProxyPage;

  if (tag) {
    return <Navigate to={new URL(tag.url).pathname} replace />;
  }

  if (content?.type === CHANNEL && content?.channel?.slug) {
    return <Channel params={{ slug: content.channel.slug }} />;
  }

  if (content?.type === VIDEO) {
    return <Video params={{ contentId: content.id }} />;
  }

  if (content?.type === STREAM) {
    return <Stream params={{ contentId: content.id }} />;
  }

  if (product) {
    return <Product params={{ productId: product.id }} />;
  }

  if (post) {
    return <Post params={{ postId: post.id }} />;
  }

  if (digitalAsset) {
    return <DigitalAsset params={{ digitalAssetId: digitalAsset.id }} />;
  }

  return null;
}

ProxyPageContent.propTypes = {
  arenaProxyPage: PropTypes.object.isRequired,
};

export default function ProxyPageDialog({ arenaProxyPage }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const arena = useArena();

  const {
    metadataTitle,
    metadataDescription,
    metadataKeywords,
    path,
    overrideTitle,
    overrideDescription,
  } = arenaProxyPage;

  const onClose = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const contextValue = useMemo(() => ({ onClose }), [onClose]);

  const overrideValue = useMemo(
    () => ({
      title: metadataTitle || null,
      description: metadataDescription || null,
      keywords: metadataKeywords || null,
      canonicalUrl: `${arena.url}${path}`,
      displayTitle: overrideTitle || null,
      displayDescription: overrideDescription || null,
    }),
    [
      metadataTitle,
      metadataDescription,
      metadataKeywords,
      arena.url,
      path,
      overrideTitle,
      overrideDescription,
    ]
  );

  const isFullscreen = Boolean(isMobile);

  const DrawerComponent = isFullscreen ? Drawer : SwipeableDrawer;

  return (
    <>
      <DrawerComponent
        open
        anchor="bottom"
        onClose={onClose}
        elevation={0}
        {...(!isFullscreen
          ? {
              onOpen: () => {},
              disableSwipeToOpen: true,
              disableDiscovery: true,
              allowSwipeInChildren: true,
            }
          : {})}
        disablePortal
        disableAutoFocus
        disableRestoreFocus
        disableEscapeKeyDown
        keepMounted
        sx={{
          zIndex: isFullscreen ? 1300 : 1099,
          top: isFullscreen ? 0 : { xs: 64, md: 64 },
          "& > .MuiDrawer-paper": {
            bgcolor: isFullscreen ? "#000" : theme.palette.background.body,
            position: "absolute",
            top: 0,
            overflowY: isFullscreen ? "hidden" : "auto",
            height: isFullscreen ? "100%" : "auto",
          },
          "& > .MuiModal-backdrop": {
            bgcolor: isFullscreen ? "#000" : theme.palette.background.body,
          },
        }}
      >
        <RouteDialogContext.Provider value={contextValue}>
          <ProxyPageOverrideContext.Provider value={overrideValue}>
            <Box
              sx={{
                overflowY: isFullscreen ? "hidden" : "auto",
                height: isFullscreen ? "100%" : "auto",
              }}
            >
              <ProxyPageContent arenaProxyPage={arenaProxyPage} />
            </Box>
          </ProxyPageOverrideContext.Provider>
        </RouteDialogContext.Provider>
      </DrawerComponent>

      {/* Canonical и meta теперь переопределяются через ProxyPageOverrideContext в ContentHelmet/ProductHelmet */}
    </>
  );
}

ProxyPageDialog.propTypes = {
  arenaProxyPage: PropTypes.object.isRequired,
};
